import React, { useState } from 'react';
import { Row, Col, Form, FormGroup, Input, InputGroup, Button, Label } from 'reactstrap';

import Body from '../Components/layout/Body';
import LearnMoreModal from '../Components/layout/LearnMoreModal';
import { USERS, USERS_STEPS } from './ConstPaths';
import { Link } from 'react-router-dom/cjs/react-router-dom';
import CalloutNotice from './CalloutNotice';

const CREATE_HELP = `
<div>
  <p><strong>Why create an account?</strong> You must be logged in to an account to save data. That allows you to do things like resume a session without losing your work, or review previous sessions. 
  (If you aren't logged in, the data is available in the browser tab but will be erased when you refresh the tab or restart your browser.)</p>
  <p><strong>We require a username and password to create an account.</strong></p>
  <p><strong>We also recommend supplying your email address.</strong> This is how you will receive a password reset if you happen to forget your password.</p>
  <p>The Preferred Name is just a short name for you - it gets displayed in the upper right corner when you are logged in. Many people use their first name for this, but you can enter anything you'd like.</p>
  <p><strong>Why a username AND an email?</strong> To allow you to protect your information, we do not <strong>require</strong> an email address for accounts. If data privacy is important to you, please note that if you do fill out the email field, it is stored in encrypted form. 
  If you include both a username and an email address in your account, you can then use your email address to login, if you prefer. Finally, you can also enter your email address in the username field, if that's preferable.</p>
  </p>  
  <p>Accounts are currently intended for AT practitioners. Clients / students that you work with are associated with your account.</p>
  <p><strong>Note: </strong>Once you've created your account, you can change the Email and Preferred Name, but you won't be able to change the Username.</p>

</div>
`;

//content sub-component with unique content for this page
function UsersCreateContent(props) {
  const {
    loggedIn,
    onChangeUsername,
    onChangePassword,
    onChangeAlias,
    onChangeEmail,
    emailValid,
    userStatus
  } = props;
  let {
    username,
    password,
    alias,
    email,
  } = props;

  const [isPwdHidden, setIsPwdHidden] = useState(true);

  const togglePwd = () => {
    setIsPwdHidden(!isPwdHidden);
  }

  //empty the fields if there is already someone logged in
  //(fields will be disabled, but we don't want to see any contents)
  if( loggedIn){
    username="";
    password="";
    alias="";
    email="";
  }
  /* invalid property turns the field border red when value is invalid (like empty).
     required property doesn't have any visual behavior -- it may announce required to screen readers
  */
  return(
    <>
    { loggedIn ?
    <Row>
      <Col>
        <CalloutNotice 
          heading={'You are already logged in'} 
          message={'To sign up for a new account, please logout first.'}
        />
      </Col>
      <Col lg="4"></Col>
    </Row>
    : <></>
    }
    <Row>
      <Col>
        <Form>
          <FormGroup>
            <Label for="username" className="aa-bold-text">
              Username <span className="aa-field-required">*</span>
            </Label>
            <Input
              id="username"
              value={username}
              onChange={onChangeUsername}
              name="username"
              type="textfield"
              invalid={userStatus === 0}
              disabled={loggedIn}
              required
            />
          </FormGroup>
          <FormGroup>
            <Label for="password" className='aa-bold-text'>
              Password <span className="aa-field-required">*</span>
            </Label>
            <InputGroup>
              <Input
                id="password"
                value={password}
                onChange={onChangePassword}
                name="password"
                type={isPwdHidden ? "password" : "text"}
                invalid={userStatus === 0}
                disabled={loggedIn}
                required
              />
              <Button outline className="aa-btn-within-field" onClick={togglePwd}
                ><span className={isPwdHidden ? 'fa fa-eye' : 'fa fa-eye-slash'}></span>
              </Button>
            </InputGroup>             
          </FormGroup>
          <FormGroup>
            <Label for="email" className='aa-bold-text'>
              Email
            </Label>
            {' '}
            <LearnMoreModal buttonClassName="aa-no-padding aa-secondary-link" linkText="" 
            title="Why we ask for your email" content="Providing your email address is optional, but it allows you to receive a new password in the future, if you happen to forget it. You can also use your email address instead of your username when you login."/>
            <Input
              id="email"
              value={email}
              onChange={onChangeEmail}
              name="email"
              type="email"
              valid={emailValid === 2}
              invalid={emailValid === 1}
              disabled={loggedIn}
            />
          </FormGroup>
          <FormGroup>
            <Label for="alias" className='aa-bold-text'>
              Preferred Name <span className="aa-more-info">(what you want to be called, like 'Emily')</span>
            </Label>
            <Input
              id="alias"
              value={alias}
              onChange={onChangeAlias}
              name="alias"
              type="textfield"
              disabled={loggedIn}
            />
          </FormGroup>
        </Form>
        <br />
        <hr />
        <Row>
          <Col>
            <p>Already have an account?&nbsp;
            <Link className="btn-link" to={USERS + "/" + USERS_STEPS.LOGIN}>Login now</Link></p>
          </Col>
        </Row>
      </Col>
      {/* puts a little margin on the right, for large screen sizes */}
      {/* <div className="col-lg-4"></div> */}
      <Col lg="4"></Col>
    </Row>
    </>
  );
}

//main component for this page
//basic pattern is to set up the pageData, define the unique content for the page (above),
//then render the page using <Body> component
function UsersCreate(props) {
  const {
    loggedIn,
    username,
    password,
    alias,
    email,
    onChangeUsername,
    onChangePassword,
    onChangeAlias,
    onChangeEmail,
    emailValid,
    userStatus,
    onContinue
  } = props;

  const pageData = {
    image: "./assets/images/target-icon.jpg", //not yet used, but could supply an image on page
    faIcon: "fa-users aa-fa-3x", //include this font-awesome icon in the image column of the page
    title: "Sign up for an account",
    subTitle: "As an AT practitioner, you can create an account in Access Navigator. This lets you save your work and access it later, on any device.",
    learnMore: {linkText: "Learn More about signing up for an account",
                title: "Signing up for an account",
                content: CREATE_HELP
                },
    nextRoute: '', //EVAL_INTRO, //static name for next page since this never changes
    sidebar: false
  }

  console.log("***rendering UsersCreate, props: " + JSON.stringify(props));

  return(
    <>
    <Body page={pageData} 
      contentComponent={
        <UsersCreateContent 
          loggedIn={loggedIn}
          username={username}
          onChangeUsername={onChangeUsername}
          password={password}
          onChangePassword={onChangePassword}
          alias={alias}
          onChangeAlias={onChangeAlias}
          email={email}
          emailValid={emailValid}
          onChangeEmail={onChangeEmail}
          userStatus={userStatus}
        />
      } 
      onContinue={onContinue}
    />
    </>
  );
}

export default UsersCreate;